import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);

// Vklad do pokladny. `movement` = existující vklad → režim úpravy (zadání 81/A).
export function DepositDialog({ open, onClose, onDone, movement = null }) {
  const { user } = useAuth();
  const edit = !!movement;
  const [team, setTeam] = useState([]);
  const [personId, setPersonId] = useState("");
  const [personName, setPersonName] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(today());
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    if (edit) {
      setAmount(String(movement.amount ?? ""));
      setDate((movement.date || today()).slice(0, 10));
      setNote(movement.note || "");
      setPersonId(movement.person_id || "other");
      setPersonName(movement.person_id ? "" : (movement.person_name || ""));
    } else {
      setAmount(""); setDate(today()); setNote("");
      setPersonId(user?.id || ""); setPersonName("");
    }
    api.get("/team").then((r) => setTeam((r.data || []).filter((m) => m.status !== "inactive"))).catch(() => setTeam([]));
  }, [open, user, edit, movement]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        amount: Number(amount) || 0, date, note: note || null,
        person_id: personId === "other" ? null : personId,
        person_name: personId === "other" ? personName.trim() : null,
      };
      if (edit) {
        await api.patch(`/cashbox/movements/${movement.id}`, payload);
        toast.success("Vklad upraven.");
      } else {
        await api.post("/cashbox/deposit", payload);
        toast.success("Vklad zaevidován.");
      }
      onDone?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="deposit-dialog">
        <DialogHeader>
          <DialogTitle>{edit ? "Upravit vklad" : "Vklad do pokladny"}</DialogTitle>
          <DialogDescription className="sr-only">Zaevidování vkladu hotovosti</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Kdo vložil</Label>
            <Select value={personId} onValueChange={setPersonId}>
              <SelectTrigger className="h-12 mt-1 w-full" data-testid="deposit-person">
                <SelectValue placeholder="Vyberte, kdo peníze vložil" />
              </SelectTrigger>
              <SelectContent>
                {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                <SelectItem value="other">Jiná osoba (mimo tým)</SelectItem>
              </SelectContent>
            </Select>
            {personId === "other" && (
              <Input value={personName} onChange={(e) => setPersonName(e.target.value)}
                placeholder="Jméno vkladatele" className="h-12 mt-2 w-full" data-testid="deposit-person-name" />
            )}
          </div>
          <div>
            <Label>Částka (Kč)</Label>
            <Input type="number" min="0" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="30 000" className="h-12 mt-1 w-full tabular-nums" data-testid="deposit-amount" autoFocus />
          </div>
          <div>
            <Label>Datum</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="h-12 mt-1 w-full" data-testid="deposit-date" />
          </div>
          <div>
            <Label>Poznámka</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="výběr z banky" className="h-12 mt-1 w-full" data-testid="deposit-note" />
          </div>
          <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} className="h-11 w-full md:w-auto" data-testid="deposit-cancel">Zrušit</Button>
            <Button onClick={save} disabled={busy || !(Number(amount) > 0) || !personId || (personId === "other" && !personName.trim())}
              className="h-11 w-full md:w-auto font-bold" data-testid="deposit-save">
              {busy ? "Ukládám…" : edit ? "Uložit změny" : "Vložit do pokladny"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Vydat zálohu / přímý výdaj. `kind` = "zaloha" | "vydaj".
export function IssueDialog({ kind, open, onClose, onDone }) {
  const { company } = useAuth();
  const [team, setTeam] = useState([]);
  const [projects, setProjects] = useState([]);
  const [personId, setPersonId] = useState("");
  const [amount, setAmount] = useState("");
  const [purposeSel, setPurposeSel] = useState("");
  const [purposeText, setPurposeText] = useState("");
  const [projectId, setProjectId] = useState("none");
  const [date, setDate] = useState(today());
  const [busy, setBusy] = useState(false);

  // Účel je výběr: Záloha na výplatu + firemní kategorie nákladů a účtenek + Jiný účel…
  const costCats = company?.cost_categories?.length ? company.cost_categories : ["Materiál", "Subdodávka", "Doprava", "Nářadí", "Ostatní"];
  const receiptCats = company?.receipt_categories?.length ? company.receipt_categories : ["PHM (pohonné hmoty)"];
  const purposeOpts = ["Záloha na výplatu", ...costCats, ...receiptCats.filter((c) => !costCats.includes(c))];
  const purpose = purposeSel === "other" ? purposeText : purposeSel;

  useEffect(() => {
    if (!open) return;
    setPersonId(""); setAmount(""); setPurposeSel(""); setPurposeText(""); setProjectId("none"); setDate(today());
    api.get("/team").then((r) => setTeam((r.data || []).filter((m) => m.status !== "inactive"))).catch(() => setTeam([]));
    api.get("/projects/options").then((r) => setProjects(r.data || [])).catch(() => setProjects([]));
  }, [open]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/cashbox/cases", {
        type: kind, person_id: personId, amount: Number(amount) || 0,
        purpose: purpose.trim(), project_id: projectId === "none" ? null : projectId, date });
      toast.success(`${data.human_id} — ${kind === "zaloha" ? "záloha vydána" : "výdaj vydán"}.`);
      if (data.warning) toast.warning(data.warning);
      onDone?.(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="issue-dialog">
        <DialogHeader>
          <DialogTitle>{kind === "zaloha" ? "Vydat zálohu" : "Přímý výdaj"}</DialogTitle>
          <DialogDescription className="sr-only">Vydání hotovosti členu týmu</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Komu</Label>
            <Select value={personId} onValueChange={setPersonId}>
              <SelectTrigger className="h-12 mt-1 w-full" data-testid="issue-person">
                <SelectValue placeholder="Vyberte člena týmu" />
              </SelectTrigger>
              <SelectContent>
                {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Částka (Kč)</Label>
            <Input type="number" min="0" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="3 000" className="h-12 mt-1 w-full tabular-nums" data-testid="issue-amount" />
          </div>
          <div>
            <Label>Účel</Label>
            <Select value={purposeSel} onValueChange={setPurposeSel}>
              <SelectTrigger className="h-12 mt-1 w-full" data-testid="issue-purpose">
                <SelectValue placeholder="Vyberte účel" />
              </SelectTrigger>
              <SelectContent>
                {purposeOpts.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                <SelectItem value="other">Jiný účel…</SelectItem>
              </SelectContent>
            </Select>
            {purposeSel === "other" && (
              <Input value={purposeText} onChange={(e) => setPurposeText(e.target.value)}
                placeholder="Popište účel" className="h-12 mt-2 w-full" data-testid="issue-purpose-text" />
            )}
          </div>
          <div>
            <Label>Zakázka (nepovinné)</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="h-12 mt-1 w-full" data-testid="issue-project">
                <SelectValue placeholder="Bez zakázky" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Bez zakázky</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Datum</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="h-12 mt-1 w-full" data-testid="issue-date" />
          </div>
          <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} className="h-11 w-full md:w-auto" data-testid="issue-cancel">Zrušit</Button>
            <Button onClick={save} disabled={busy || !personId || !(Number(amount) > 0) || !purpose.trim()}
              className="h-11 w-full md:w-auto font-bold" data-testid="issue-save">
              {busy ? "Ukládám…" : kind === "zaloha" ? "Vydat zálohu" : "Vydat hotovost"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
